import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import logo from "../assets/logo.png";

export default function Login() {
  const { loginWithGoogle, loginWithEmail, registerWithEmail } = useAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [googleMessage, setGoogleMessage] = useState(null); // { type: "error" | "info", text }
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = location.state?.from || "/account";

  const handleGoogle = async () => {
    setGoogleMessage(null);
    setLoading(true);
    try {
      await loginWithGoogle();
      navigate(redirectTo);
    } catch (err) {
      console.error(err);
      if (err.code === "auth/popup-closed-by-user") {
        // user backed out — not worth surfacing
      } else if (err.code === "auth/link-email-sent") {
        setGoogleMessage({ type: "info", text: err.message });
      } else if (err.code === "auth/account-exists-with-different-credential") {
        setGoogleMessage({ type: "error", text: "An account already exists for this email using a different sign-in method." });
      } else {
        setGoogleMessage({ type: "error", text: "Google sign-in failed. Try again." });
      }
    }
    setLoading(false);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setError("");
    if (mode === "register" && password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "register") {
        await registerWithEmail(email.trim(), password, displayName.trim());
      } else {
        await loginWithEmail(email.trim(), password);
      }
      navigate(redirectTo);
    } catch (err) {
      console.error(err);
      const messages = {
        "auth/email-already-in-use": "An account with this email already exists.",
        "auth/invalid-email": "Invalid email address.",
        "auth/weak-password": "Password must be at least 6 characters.",
        "auth/user-not-found": "No account found with this email.",
        "auth/wrong-password": "Incorrect password.",
        "auth/invalid-credential": "Incorrect email or password.",
      };
      setError(messages[err.code] || "Something went wrong. Try again.");
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-6">
      <Link to="/" className="text-gray-600 text-sm mb-8 no-underline hover:text-gray-400">← Back</Link>

      <div className="text-center mb-8">
        <img src={logo} alt="" className="w-full max-w-xs mx-auto" />
        <div className="flex items-center gap-3 max-w-[280px] mx-auto mt-2 mb-3">
          <div className="flex-1 h-px bg-dark-500" />
          <span className="text-gray-400 text-[10px] font-display font-bold tracking-[0.25em] whitespace-nowrap">
            STUDY &bull; FENCE &bull; GROW
          </span>
          <div className="flex-1 h-px bg-dark-500" />
        </div>
        <h1 className="font-display text-2xl font-bold">
          {mode === "register" ? "Create Account" : "Join the Community"}
        </h1>
        <p className="text-gray-500 text-sm mt-1 max-w-xs mx-auto">Create and run events, track your judging history, and follow your competition results.</p>
      </div>

      <div className="w-full max-w-xs flex flex-col gap-4">
        {/* Google */}
        <button onClick={handleGoogle} disabled={loading}
          className="w-full p-3.5 bg-white text-gray-800 font-display text-sm font-bold rounded-xl border-none cursor-pointer flex items-center justify-center gap-3 disabled:opacity-40 hover:bg-gray-100">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 010-9.18l-7.98-6.19a24.003 24.003 0 000 21.56l7.98-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
          Continue with Google
        </button>
        {googleMessage && (
            <div className={`text-sm text-center rounded-lg p-2 ${googleMessage.type === "info" ? "text-green-action-text bg-green-900 bg-opacity-20" : "text-red-400 bg-red-900 bg-opacity-20"}`}>
            {googleMessage.text}
          </div>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-dark-500" />
          <span className="text-gray-600 text-xs">or</span>
          <div className="flex-1 h-px bg-dark-500" />
        </div>

        {/* Email/Password */}
        <form onSubmit={handleEmailSubmit} className="flex flex-col gap-3">
          {mode === "register" && (
            <div className="flex flex-col gap-1">
            <label htmlFor="login-name" className="text-sm text-gray-300">Your name</label>
            <input id="login-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name" type="text"
              className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />
            </div>
          )}
          <label htmlFor="login-email" className="text-sm text-gray-300">Email</label>
          <input id="login-email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" type="email" autoComplete="email"
            className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />
          <div className="relative">
            <label htmlFor="login-password" className="text-sm text-gray-300">Password</label>
            <input id="login-password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password" type={showPassword ? "text" : "password"} autoComplete={mode === "register" ? "new-password" : "current-password"}
              className="w-full p-3 pr-11 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />
            <button type="button" onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-0 top-0 h-full px-3 bg-transparent border-none text-gray-500 cursor-pointer hover:text-gray-300">
              {showPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a20.4 20.4 0 015.06-6.06M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a20.4 20.4 0 01-2.88 4.09M14.12 14.12a3 3 0 11-4.24-4.24" strokeLinecap="round" strokeLinejoin="round"/><path d="M1 1l22 22" strokeLinecap="round"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="3"/></svg>
              )}
            </button>
          </div>
          {mode === "register" && (
            <div className="flex flex-col gap-1">
            <label htmlFor="login-confirm-password" className="text-sm text-gray-300">Confirm password</label>
            <input id="login-confirm-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password" type={showPassword ? "text" : "password"} autoComplete="new-password"
              className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />
            </div>
          )}

          {error && <div role="alert" className="text-red-400 text-sm text-center bg-red-900 bg-opacity-20 rounded-lg p-2">{error}</div>}

          <button type="submit" disabled={loading || !email.trim() || !password || (mode === "register" && !confirmPassword)}
            className="w-full p-3.5 bg-gradient-to-br from-green-action-light to-green-action text-white font-display text-sm font-bold rounded-xl tracking-wide disabled:opacity-40 border-none cursor-pointer">
            {loading ? "Working..." : mode === "register" ? "Create Account" : "Sign In"}
          </button>
        </form>

        {/* Toggle */}
        <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setConfirmPassword(""); }}
          className="bg-transparent border-none text-gray-500 text-sm cursor-pointer hover:text-gray-300">
          {mode === "login" ? "Don't have an account? Create one" : "Already have an account? Sign in"}
        </button>
      </div>
    </div>
  );
}
